import { Fragment, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Popconfirm, Select } from "antd";
import {
  ChevronDown,
  ChevronRight,
  CheckCircle2,
  Edit2,
  Mail,
  MailCheck,
  Trash,
} from "lucide-react";
import CustomButton from "@/components/common/Button";
import { notify } from "@/components/common/Notification";
import { PtsBillingService, PtsProjectService } from "@/services/APIs";
import { Button } from "../../../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/ui/table";
import {
  BILLING_CONFIGURATIONS_QUERY_KEY,
  formatBillingConfigurationItemGstRates,
  getBillingConfigurationTotalAmount,
  parseBillingAmount,
  resolveProjectLabel,
  type BillingConfigurationApiItem,
  type BillingConfigurationApiRecord,
} from "./billingListUtils";
import BillingInvoicePagination from "./BillingInvoicePagination";
import { BILLING_DEFAULT_PAGE_SIZE } from "./billingPaginationUtils";
import {
  INVOICE_PREVIEWS_QUERY_KEY,
  formatInvoiceItemTaxLabel,
  formatInvoiceItemBillingPeriod,
  formatInvoiceListDate,
  getInvoiceItemTaxAmount,
  normalizeInvoiceFollowupStatus,
  normalizeInvoiceRecord,
  parseInvoiceAmount,
  parseInvoiceDetails,
  parseInvoiceItemDescription,
  type InvoiceApiRecord,
} from "./invoiceListUtils";
import { INVOICE_FROM } from "./data";
import InvoiceMailSentConfirmModal from "./InvoiceMailSentConfirmModal";
import InvoiceNumberUpdateModal from "./InvoiceNumberUpdateModal";
import InvoiceFollowupConfirmModal from "./InvoiceFollowupConfirmModal";
import InvoiceMailSendingOverlay, {
  type InvoiceMailSendingOverlayStatus,
} from "./InvoiceMailSendingOverlay";
import { Invoice, InvoiceAddressBlock, INVOICE_STATUS_OPTIONS } from "./types";
import { inr, invoiceStatusBadge } from "./utils";

const BILLING_CONFIG_COLUMN_COUNT = 7;
const INVOICE_PREVIEW_COLUMN_COUNT = 11;
const INVOICE_PROJECT_FILTER_SELECT_CLASS =
  "w-full min-w-[200px] [&_.ant-select-selector]:!h-9 [&_.ant-select-selector]:!min-h-9 [&_.ant-select-selector]:!items-center [&_.ant-select-selector]:!rounded-lg [&_.ant-select-selector]:!border-gray-200 [&_.ant-select-selector]:!bg-[#fafafa] [&_.ant-select-selector]:!px-3 [&_.ant-select-selection-item]:!leading-7 [&_.ant-select-selection-placeholder]:!leading-7";
const INVOICE_ACTIONS_WIDTH_CLASS = "w-[152px]";
const INVOICE_FOLLOWUP_WIDTH_CLASS = "w-[120px]";
const INVOICE_MAIL_ACTION_BTN_CLASS =
  "h-8 w-[112px] shrink-0 gap-1.5 px-2 text-xs";
const INVOICE_FOLLOWUP_ACTION_BTN_CLASS =
  "h-8 w-[88px] shrink-0 gap-1.5 px-2 text-xs";
const INVOICE_ACTION_ICON_SLOT_CLASS =
  "inline-flex h-8 w-8 shrink-0 items-center justify-center";
const MAIL_SENT_HOLD_AFTER_API_MS = 2000;
const MAIL_SENT_SUCCESS_DISPLAY_MS = 2000;

function InvoiceAddressCard({
  label,
  address,
}: {
  label: string;
  address: InvoiceAddressBlock;
}) {
  return (
    <div className="space-y-0.5 rounded-md border border-gray-300 p-3 text-xs">
      <p className="text-muted-foreground font-semibold">{label}</p>
      <p className="text-foreground font-semibold">{address.name}</p>
      {address.lines.map(line => (
        <p key={`${label}-${line}`}>{line}</p>
      ))}
      {address.gstin ? <p className="mt-1">GST NO: {address.gstin}</p> : null}
    </div>
  );
}

function InvoiceDetailsAddressBlock({
  label,
  details,
}: {
  label: string;
  details?: string;
}) {
  const parsed = parseInvoiceDetails(details);

  return (
    <InvoiceAddressCard
      label={label}
      address={{
        name: parsed.name,
        lines: parsed.lines,
        gstin: parsed.gstin,
      }}
    />
  );
}

function InvoicePreviewExpandedDetails({
  record,
}: {
  record: InvoiceApiRecord;
}) {
  const items = Array.isArray(record.items) ? record.items : [];
  const invoice = normalizeInvoiceRecord(record);

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="bg-muted/30 border-b border-gray-200 px-4 py-3">
        <div>
          <p className="text-primary text-sm font-bold uppercase tracking-wide">
            {record.invoice_title?.trim() ?? "Tax Invoice"}
          </p>
          <p className="text-muted-foreground mt-1 text-xs">
            Invoice #{invoice.id} · {formatInvoiceListDate(record.invoice_date)}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 border-b border-gray-200 p-4 md:grid-cols-2">
        <InvoiceAddressCard label="FROM" address={INVOICE_FROM} />
        <InvoiceDetailsAddressBlock
          label="BILL TO"
          details={record.to_details}
        />
      </div>

      <div className="p-4">
        <div className="overflow-hidden rounded-md border border-gray-200">
          <Table className="[&_tbody_tr]:border-0">
            <TableHeader>
              <TableRow className="bg-muted/60 hover:bg-muted/60 border-b border-gray-300">
                <TableHead className="text-primary w-10 px-3">#</TableHead>
                <TableHead className="text-primary min-w-[200px] px-3">
                  Description
                </TableHead>
                <TableHead className="text-primary px-3">HSN/SAC</TableHead>
                <TableHead className="text-primary px-3 text-right">
                  Qty
                </TableHead>
                <TableHead className="text-primary px-3 text-right">
                  Rate
                </TableHead>
                <TableHead className="text-primary px-3 text-right">
                  Amount
                </TableHead>
                <TableHead className="text-primary px-3 text-right">
                  Total
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    className="text-muted-foreground py-8 text-center text-sm"
                  >
                    No line items found
                  </TableCell>
                </TableRow>
              ) : (
                items.map((item, itemIndex) => {
                  const taxLabel = formatInvoiceItemTaxLabel(item);
                  const taxAmount = getInvoiceItemTaxAmount(item);
                  const itemDetails = parseInvoiceItemDescription(
                    item.description,
                    invoice.period !== "—" ? invoice.period : undefined
                  );
                  const billingPeriod =
                    formatInvoiceItemBillingPeriod(item) ||
                    itemDetails.billingPeriod;

                  return (
                    <TableRow
                      key={item.id ?? `invoice-item-${itemIndex}`}
                      className="hover:bg-transparent"
                    >
                      <TableCell className="px-3 align-top font-medium">
                        {itemIndex + 1}
                      </TableCell>
                      <TableCell className="px-3 align-top">
                        <p className="text-sm font-medium">
                          {itemDetails.title}
                        </p>
                        {itemDetails.subtitle || billingPeriod ? (
                          <p className="mt-1 text-xs">
                            {billingPeriod ? (
                              <span className="text-foreground font-medium">
                                ({billingPeriod})
                              </span>
                            ) : null}
                            {itemDetails.subtitle ? (
                              <span className="text-muted-foreground">
                                {billingPeriod ? " · " : ""}
                                {itemDetails.subtitle}
                              </span>
                            ) : null}
                          </p>
                        ) : null}
                        {taxLabel !== "—" ? (
                          <p className="text-muted-foreground mt-1 text-xs">
                            {taxLabel}
                            {taxAmount > 0 ? ` · ${inr(taxAmount)}` : ""}
                          </p>
                        ) : null}
                      </TableCell>
                      <TableCell className="px-3 align-top text-blue-600">
                        {item.hsn_sac?.trim() ?? "—"}
                      </TableCell>
                      <TableCell className="px-3 text-right align-top">
                        {item.qty ?? "—"}
                      </TableCell>
                      <TableCell className="px-3 text-right align-top">
                        {inr(parseInvoiceAmount(item.rate))}
                      </TableCell>
                      <TableCell className="px-3 text-right align-top">
                        {inr(parseInvoiceAmount(item.amount))}
                      </TableCell>
                      <TableCell className="px-3 text-right align-top font-semibold">
                        {inr(parseInvoiceAmount(item.total))}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>

        <div className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          {record.notes?.trim() ? (
            <div className="bg-muted/20 max-w-xl rounded-md border border-gray-200 px-3 py-2 text-xs">
              <p className="text-muted-foreground font-semibold">Notes</p>
              <p className="text-foreground mt-1 leading-relaxed">
                {record.notes.trim()}
              </p>
            </div>
          ) : (
            <div />
          )}

          <div className="w-full space-y-1.5 text-sm sm:w-64">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Sub Total</span>
              <span>{inr(invoice.amount)}</span>
            </div>
            {invoice.gst > 0 ? (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Tax Total</span>
                <span>{inr(invoice.gst)}</span>
              </div>
            ) : null}
            <div className="flex justify-between border-t border-gray-200 pt-2 text-base font-semibold">
              <span>Total</span>
              <span>{inr(invoice.total)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

interface BillingConfigListingSectionProps {
  onEdit: (record: BillingConfigurationApiRecord) => void;
  onDelete: (record: BillingConfigurationApiRecord) => void;
  editingConfigurationId?: string | null;
}

function BillingConfigExpandedDetails({
  record,
}: {
  record: BillingConfigurationApiRecord;
}) {
  const items = Array.isArray(record.items) ? record.items : [];
  const totalAmount = getBillingConfigurationTotalAmount(record);

  return (
    <div className="w-full overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="bg-muted/30 flex flex-col gap-3 border-b border-gray-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-primary text-sm font-bold uppercase tracking-wide">
            Billing Items
          </p>
          <p className="text-muted-foreground mt-1 text-xs">
            {record.client_name?.trim() ?? "—"} · {items.length} item
            {items.length === 1 ? "" : "s"}
          </p>
        </div>
        <div className="sm:text-right">
          <p className="text-muted-foreground text-xs">Total Amount</p>
          <p className="text-base font-semibold">{inr(totalAmount)}</p>
        </div>
      </div>

      <div className="p-4">
        <div className="overflow-x-auto rounded-md border border-gray-200">
          <Table className="w-full min-w-[920px] [&_tbody_tr]:border-0">
            <TableHeader>
              <TableRow className="bg-muted/60 hover:bg-muted/60 border-b border-gray-300">
                <TableHead className="text-primary w-10 px-3">#</TableHead>
                <TableHead className="text-primary px-3">
                  Billing Type
                </TableHead>
                <TableHead className="text-primary px-3">Frequency</TableHead>
                <TableHead className="text-primary px-3">Start Date</TableHead>
                <TableHead className="text-primary px-3 text-right">
                  Amount
                </TableHead>
                <TableHead className="text-primary px-3">Currency</TableHead>
                <TableHead className="text-primary px-3">HSN/SAC</TableHead>
                <TableHead className="text-primary px-3 text-right">
                  Qty
                </TableHead>
                <TableHead className="text-primary px-3 text-right">
                  CGST
                </TableHead>
                <TableHead className="text-primary px-3 text-right">
                  SGST
                </TableHead>
                <TableHead className="text-primary px-3 text-right">
                  IGST
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={11}
                    className="text-muted-foreground py-8 text-center text-sm"
                  >
                    No billing items found
                  </TableCell>
                </TableRow>
              ) : (
                items.map((item: BillingConfigurationApiItem, itemIndex) => {
                  const gstRates = formatBillingConfigurationItemGstRates(item);
                  const itemKey = [
                    record.id ?? "config",
                    item.billing_type,
                    item.frequency,
                    item.binning_start_date,
                    item.amount,
                    item.hsn_sac,
                    item.qty,
                  ]
                    .map(value => String(value ?? "").trim())
                    .join("|");

                  return (
                    <TableRow key={itemKey} className="hover:bg-transparent">
                      <TableCell className="px-3 align-top font-medium">
                        {itemIndex + 1}
                      </TableCell>
                      <TableCell className="px-3 align-top font-medium">
                        {item.billing_type?.trim() ?? "—"}
                      </TableCell>
                      <TableCell className="px-3 align-top text-blue-600">
                        {item.frequency?.trim() ?? "—"}
                      </TableCell>
                      <TableCell className="px-3 align-top">
                        {item.binning_start_date?.trim()
                          ? formatInvoiceListDate(item.binning_start_date)
                          : "—"}
                      </TableCell>
                      <TableCell className="px-3 text-right align-top font-semibold">
                        {inr(parseBillingAmount(item.amount))}
                      </TableCell>
                      <TableCell className="px-3 align-top">
                        {item.currency?.trim() ?? "—"}
                      </TableCell>
                      <TableCell className="px-3 align-top text-blue-600">
                        {item.hsn_sac?.trim() ?? "—"}
                      </TableCell>
                      <TableCell className="px-3 text-right align-top">
                        {item.qty ?? "—"}
                      </TableCell>
                      <TableCell className="whitespace-nowrap px-3 text-right align-top">
                        {gstRates.cgstRate}
                      </TableCell>
                      <TableCell className="whitespace-nowrap px-3 text-right align-top">
                        {gstRates.sgstRate}
                      </TableCell>
                      <TableCell className="whitespace-nowrap px-3 text-right align-top">
                        {gstRates.igstRate}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}

export const BillingConfigListingSection = ({
  onEdit,
  onDelete,
  editingConfigurationId,
}: BillingConfigListingSectionProps) => {
  const [expandedConfigId, setExpandedConfigId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(BILLING_DEFAULT_PAGE_SIZE);
  const [projectId, setProjectId] = useState<string | undefined>(undefined);

  const { data: projectOptions = [], isLoading: projectLoading } = useQuery({
    queryKey: ["open-project-list"],
    queryFn: () => PtsProjectService.getOpenProjectList(),
  });

  const projectSelectOptions = useMemo(
    () =>
      projectOptions.map(option => ({
        value: option.value,
        label: option.label,
      })),
    [projectOptions]
  );

  const {
    data: billingData,
    isLoading,
    isError,
  } = useQuery({
    queryKey: [...BILLING_CONFIGURATIONS_QUERY_KEY, projectId, page, pageSize],
    queryFn: () =>
      PtsBillingService.getBillingConfigurationRecords(
        page,
        pageSize,
        projectId
      ),
  });

  const billingRecords = billingData?.items ?? [];
  const totalCount = billingData?.count ?? 0;

  const toggleExpanded = (recordId: string) => {
    setExpandedConfigId(current => (current === recordId ? null : recordId));
  };

  return (
    <Card className="border border-gray-200 bg-white">
      <CardHeader className="flex-row items-start justify-between gap-4 space-y-0">
        <div className="min-w-0">
          <CardTitle className="text-lg">
            Billing Configuration Listing
          </CardTitle>
          <CardDescription>
            All saved billing configurations across projects. Click a row to
            view billing items.
          </CardDescription>
        </div>
        <div className="w-full max-w-[240px] shrink-0">
          <Select
            allowClear
            placeholder={
              projectLoading ? "Loading projects..." : "All projects"
            }
            value={projectId ?? undefined}
            onChange={value => {
              setProjectId(value || undefined);
              setPage(1);
            }}
            className={INVOICE_PROJECT_FILTER_SELECT_CLASS}
            style={{ width: "100%" }}
            showSearch
            loading={projectLoading}
            optionFilterProp="label"
            filterOption={(input, option) =>
              String(option?.label ?? "")
                .toLowerCase()
                .includes(input.toLowerCase())
            }
            options={projectSelectOptions}
            notFoundContent="No projects found"
            listHeight={280}
            popupMatchSelectWidth
            getPopupContainer={trigger =>
              trigger.parentElement ?? document.body
            }
          />
        </div>
      </CardHeader>
      <CardContent className="p-0 sm:p-0">
        <div className="overflow-x-auto px-0 sm:px-6">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10" />
                <TableHead className="text-primary w-12 px-3 font-semibold">
                  #
                </TableHead>
                <TableHead className="min-w-[180px] px-3">Project</TableHead>
                <TableHead className="min-w-[140px] px-3">Client</TableHead>
                <TableHead className="min-w-[120px] px-3 text-right">
                  Total Amount
                </TableHead>
                <TableHead className="w-28 px-2 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell
                    colSpan={BILLING_CONFIG_COLUMN_COUNT}
                    className="text-muted-foreground py-8 text-center text-sm"
                  >
                    Loading billing configurations...
                  </TableCell>
                </TableRow>
              ) : isError ? (
                <TableRow>
                  <TableCell
                    colSpan={BILLING_CONFIG_COLUMN_COUNT}
                    className="text-muted-foreground py-8 text-center text-sm"
                  >
                    Failed to load billing configurations
                  </TableCell>
                </TableRow>
              ) : billingRecords.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={BILLING_CONFIG_COLUMN_COUNT}
                    className="text-muted-foreground py-8 text-center text-sm"
                  >
                    No billing configurations found
                  </TableCell>
                </TableRow>
              ) : (
                billingRecords.map((record, index) => {
                  const recordId =
                    record.id?.trim() ?? `billing-config-${index}`;
                  const isExpanded = expandedConfigId === recordId;
                  const isEditing = editingConfigurationId === record.id;
                  const itemCount = Array.isArray(record.items)
                    ? record.items.length
                    : 0;
                  const totalAmount =
                    getBillingConfigurationTotalAmount(record);

                  return (
                    <Fragment key={recordId}>
                      <TableRow
                        className={`cursor-pointer ${
                          isExpanded || isEditing
                            ? "bg-primary/5 hover:bg-primary/5"
                            : "hover:bg-muted/40"
                        }`}
                        onClick={() => toggleExpanded(recordId)}
                      >
                        <TableCell className="w-10 px-2">
                          {isExpanded ? (
                            <ChevronDown className="text-primary h-4 w-4" />
                          ) : (
                            <ChevronRight className="text-muted-foreground h-4 w-4" />
                          )}
                        </TableCell>
                        <TableCell className="text-primary px-3 font-semibold">
                          {index + 1}
                        </TableCell>
                        <TableCell className="truncate px-3">
                          <div className="text-primary font-semibold">
                            {resolveProjectLabel(
                              record.project,
                              projectOptions
                            )}
                          </div>
                          {itemCount > 0 ? (
                            <div className="text-muted-foreground mt-0.5 text-xs font-normal">
                              {itemCount} item{itemCount > 1 ? "s" : ""}
                            </div>
                          ) : null}
                        </TableCell>
                        <TableCell className="truncate px-3">
                          {record.client_name?.trim() ?? "—"}
                        </TableCell>
                        <TableCell className="whitespace-nowrap px-3 text-right font-semibold">
                          {inr(totalAmount)}
                        </TableCell>
                        <TableCell
                          className="px-2 py-2"
                          onClick={event => event.stopPropagation()}
                        >
                          <div className="flex items-center justify-end gap-0">
                            <CustomButton
                              icon={<Edit2 size={14} />}
                              type="text"
                              onClick={() => onEdit(record)}
                              title="Edit"
                            />
                            <Popconfirm
                              title="Are you sure you want to delete this record?"
                              onConfirm={() => onDelete(record)}
                              okText="Yes"
                              cancelText="No"
                            >
                              <CustomButton
                                danger
                                icon={<Trash size={14} />}
                                type="text"
                                title="Delete"
                              />
                            </Popconfirm>
                          </div>
                        </TableCell>
                      </TableRow>
                      {isExpanded ? (
                        <TableRow className="bg-primary/5 hover:bg-primary/5">
                          <TableCell
                            colSpan={BILLING_CONFIG_COLUMN_COUNT}
                            className="bg-muted/10 p-0"
                          >
                            <div className="px-3 py-3 sm:px-4">
                              <BillingConfigExpandedDetails record={record} />
                            </div>
                          </TableCell>
                        </TableRow>
                      ) : null}
                    </Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
        <BillingInvoicePagination
          current={page}
          pageSize={pageSize}
          total={totalCount}
          onChange={(nextPage, nextPageSize) => {
            setPage(nextPage);
            setPageSize(nextPageSize);
          }}
          disabled={isLoading}
        />
      </CardContent>
    </Card>
  );
};

export const InvoicePreviewListingSection = () => {
  const queryClient = useQueryClient();
  const [expandedInvoiceId, setExpandedInvoiceId] = useState<string | null>(
    null
  );
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(BILLING_DEFAULT_PAGE_SIZE);
  const [projectId, setProjectId] = useState<string | undefined>(undefined);
  const [status, setStatus] = useState<string | undefined>(undefined);
  const [mailSentInvoice, setMailSentInvoice] = useState<{
    recordId: string;
    invoice: Invoice;
  } | null>(null);
  const [invoiceNumberUpdateInvoice, setInvoiceNumberUpdateInvoice] = useState<{
    recordId: string;
    invoice: Invoice;
  } | null>(null);
  const [invoiceNumberError, setInvoiceNumberError] = useState<string | null>(
    null
  );
  const [mailSendingOverlay, setMailSendingOverlay] =
    useState<InvoiceMailSendingOverlayStatus | null>(null);
  const [followupInvoice, setFollowupInvoice] = useState<{
    recordId: string;
    invoice: Invoice;
  } | null>(null);
  const [followupSendingOverlay, setFollowupSendingOverlay] =
    useState<InvoiceMailSendingOverlayStatus | null>(null);

  const { data: projectOptions = [], isLoading: projectLoading } = useQuery({
    queryKey: ["open-project-list"],
    queryFn: () => PtsProjectService.getOpenProjectList(),
  });

  const projectSelectOptions = useMemo(
    () =>
      projectOptions.map(option => ({
        value: option.value,
        label: option.label,
      })),
    [projectOptions]
  );

  const {
    data: invoiceData,
    isLoading,
    isError,
  } = useQuery({
    queryKey: [
      ...INVOICE_PREVIEWS_QUERY_KEY,
      projectId,
      status,
      page,
      pageSize,
    ],
    queryFn: () =>
      PtsBillingService.getInvoiceRecords(page, pageSize, projectId, status),
  });

  const invoiceRecords = invoiceData?.items ?? [];
  const totalCount = invoiceData?.count ?? 0;

  const mailSentMutation = useMutation({
    mutationFn: (recordId: string) =>
      PtsBillingService.updateInvoiceStatus(recordId, "Sent"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INVOICE_PREVIEWS_QUERY_KEY });

      window.setTimeout(() => {
        setMailSendingOverlay("success");

        window.setTimeout(() => {
          setMailSendingOverlay(null);
          setMailSentInvoice(null);
        }, MAIL_SENT_SUCCESS_DISPLAY_MS);
      }, MAIL_SENT_HOLD_AFTER_API_MS);
    },
    onError: () => {
      setMailSendingOverlay(null);
      notify.error("Failed to mark invoice as sent. Please try again.");
    },
  });

  const updateInvoiceNumberMutation = useMutation({
    mutationFn: ({
      recordId,
      invoiceNumber,
    }: {
      recordId: string;
      invoiceNumber: string;
      invoice: Invoice;
    }) => PtsBillingService.updateInvoiceNumber(recordId, invoiceNumber),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: INVOICE_PREVIEWS_QUERY_KEY });
      notify.success("Invoice number updated successfully");
      setInvoiceNumberError(null);
      setInvoiceNumberUpdateInvoice(null);
      setMailSentInvoice({
        recordId: variables.recordId,
        invoice: {
          ...variables.invoice,
          id: variables.invoiceNumber,
        },
      });
    },
    onError: (error: unknown) => {
      const apiMessage =
        (
          error as {
            response?: {
              data?: { error?: { message?: string }; message?: string };
            };
          }
        )?.response?.data?.error?.message ??
        (
          error as {
            response?: { data?: { message?: string } };
          }
        )?.response?.data?.message ??
        null;

      setInvoiceNumberError(
        apiMessage ?? "Failed to update invoice number. Please try again."
      );
    },
  });

  const deleteInvoiceMutation = useMutation({
    mutationFn: (recordId: string) => PtsBillingService.deleteInvoice(recordId),
    onSuccess: (_data, recordId) => {
      queryClient.invalidateQueries({ queryKey: INVOICE_PREVIEWS_QUERY_KEY });
      notify.success("Invoice deleted");
      setExpandedInvoiceId(current => (current === recordId ? null : current));
      setMailSentInvoice(current =>
        current?.recordId === recordId ? null : current
      );
      setInvoiceNumberUpdateInvoice(current =>
        current?.recordId === recordId ? null : current
      );
      setFollowupInvoice(current =>
        current?.recordId === recordId ? null : current
      );
    },
    onError: () => {
      notify.error("Failed to delete invoice. Please try again.");
    },
  });

  const followupMutation = useMutation({
    mutationFn: (recordId: string) =>
      PtsBillingService.sendInvoiceFollowup(recordId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INVOICE_PREVIEWS_QUERY_KEY });

      window.setTimeout(() => {
        setFollowupSendingOverlay("success");

        window.setTimeout(() => {
          setFollowupSendingOverlay(null);
          setFollowupInvoice(null);
        }, MAIL_SENT_SUCCESS_DISPLAY_MS);
      }, MAIL_SENT_HOLD_AFTER_API_MS);
    },
    onError: () => {
      setFollowupSendingOverlay(null);
      notify.error("Failed to send invoice follow-up. Please try again.");
    },
  });

  const toggleExpanded = (recordId: string) => {
    setExpandedInvoiceId(current => (current === recordId ? null : recordId));
  };

  const handleConfirmMailSent = () => {
    if (!mailSentInvoice?.recordId || mailSentMutation.isPending) {
      return;
    }

    setMailSendingOverlay("sending");
    mailSentMutation.mutate(mailSentInvoice.recordId);
  };

  const handleSaveInvoiceNumber = (invoiceNumber: string) => {
    if (
      !invoiceNumberUpdateInvoice?.recordId ||
      updateInvoiceNumberMutation.isPending
    ) {
      return;
    }

    updateInvoiceNumberMutation.mutate({
      recordId: invoiceNumberUpdateInvoice.recordId,
      invoiceNumber,
      invoice: invoiceNumberUpdateInvoice.invoice,
    });
  };

  const handleMailSentClick = (
    recordId: string,
    invoice: Invoice,
    options: { isRecurring: boolean; hasInvoiceNumber: boolean }
  ) => {
    // Recurring + missing invoice number → enter number first; otherwise confirm directly
    if (options.isRecurring && !options.hasInvoiceNumber) {
      setInvoiceNumberUpdateInvoice({ recordId, invoice });
      return;
    }

    setMailSentInvoice({ recordId, invoice });
  };

  const handleConfirmFollowup = () => {
    if (!followupInvoice?.recordId || followupMutation.isPending) {
      return;
    }

    setFollowupSendingOverlay("sending");
    followupMutation.mutate(followupInvoice.recordId);
  };

  return (
    <>
      <Card className="border border-gray-200 bg-white">
        <CardHeader className="flex-col items-start justify-between gap-4 space-y-0 p-3 sm:flex-row md:p-6">
          <div className="min-w-0">
            <CardTitle className="text-lg">Invoice Preview Listing</CardTitle>
            <CardDescription>
              All generated invoice previews. Click a row to view item details.
            </CardDescription>
          </div>
          <div className="flex w-full max-w-[500px] shrink-0 flex-col gap-2 sm:flex-row">
            <Select
              allowClear
              placeholder={
                projectLoading ? "Loading projects..." : "All projects"
              }
              value={projectId ?? undefined}
              onChange={value => {
                setProjectId(value || undefined);
                setPage(1);
              }}
              className={INVOICE_PROJECT_FILTER_SELECT_CLASS}
              style={{ width: "100%" }}
              showSearch
              loading={projectLoading}
              optionFilterProp="label"
              filterOption={(input, option) =>
                String(option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              options={projectSelectOptions}
              notFoundContent="No projects found"
              listHeight={280}
              popupMatchSelectWidth
              getPopupContainer={trigger =>
                trigger.parentElement ?? document.body
              }
            />
            <Select
              allowClear
              placeholder="All statuses"
              value={status ?? undefined}
              onChange={value => {
                setStatus(value || undefined);
                setPage(1);
              }}
              className={INVOICE_PROJECT_FILTER_SELECT_CLASS}
              style={{ width: "100%" }}
              options={INVOICE_STATUS_OPTIONS.map(option => ({
                value: option,
                label: option,
              }))}
              listHeight={280}
              popupMatchSelectWidth
              getPopupContainer={trigger =>
                trigger.parentElement ?? document.body
              }
            />
          </div>
        </CardHeader>
        <CardContent className="mt-2 p-0 sm:p-0">
          <div className="overflow-x-auto px-0 sm:px-6">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10" />
                  <TableHead className="text-primary font-semibold">
                    Invoice #
                  </TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead className="min-w-[180px]">Project</TableHead>
                  <TableHead>Period</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead className="text-right">GST</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead
                    className={`${INVOICE_FOLLOWUP_WIDTH_CLASS} text-center`}
                  >
                    Followup
                  </TableHead>
                  <TableHead
                    className={`${INVOICE_ACTIONS_WIDTH_CLASS} pr-3 text-right`}
                  >
                    Actions
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell
                      colSpan={INVOICE_PREVIEW_COLUMN_COUNT}
                      className="text-muted-foreground py-8 text-center text-sm"
                    >
                      Loading invoice previews...
                    </TableCell>
                  </TableRow>
                ) : isError ? (
                  <TableRow>
                    <TableCell
                      colSpan={INVOICE_PREVIEW_COLUMN_COUNT}
                      className="text-muted-foreground py-8 text-center text-sm"
                    >
                      Failed to load invoice previews
                    </TableCell>
                  </TableRow>
                ) : invoiceRecords.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={INVOICE_PREVIEW_COLUMN_COUNT}
                      className="text-muted-foreground py-8 text-center text-sm"
                    >
                      No invoice previews found
                    </TableCell>
                  </TableRow>
                ) : (
                  invoiceRecords.map((record, index) => {
                    const invoice = normalizeInvoiceRecord(record);
                    const recordId =
                      record.id?.trim() ?? `${invoice.id}-${index}`;
                    const apiRecordId = record.id?.trim();
                    const isExpanded = expandedInvoiceId === recordId;
                    const itemCount = Array.isArray(record.items)
                      ? record.items.length
                      : 0;
                    const canMarkAsSent =
                      Boolean(apiRecordId) && invoice.status === "Draft";
                    const isSent = invoice.status === "Sent";
                    const followupStatus = normalizeInvoiceFollowupStatus(
                      record.followup_status
                    );

                    return (
                      <Fragment key={recordId}>
                        <TableRow
                          className={`cursor-pointer ${
                            isExpanded
                              ? "bg-primary/5 hover:bg-primary/5"
                              : "hover:bg-muted/40"
                          } ${
                            record.is_recurring
                              ? "border-secondary bg-secondary/10 hover:bg-secondary/20 border-l-4"
                              : ""
                          }`}
                          onClick={() => toggleExpanded(recordId)}
                        >
                          <TableCell className="w-10 px-2">
                            {isExpanded ? (
                              <ChevronDown className="text-primary h-4 w-4" />
                            ) : (
                              <ChevronRight className="text-muted-foreground h-4 w-4" />
                            )}
                          </TableCell>
                          <TableCell className="text-primary font-semibold">
                            <div>{invoice.id}</div>
                            {itemCount > 0 ? (
                              <div className="text-muted-foreground mt-0.5 text-xs font-normal">
                                {itemCount} item{itemCount > 1 ? "s" : ""}
                              </div>
                            ) : null}
                          </TableCell>
                          <TableCell>{invoice.client}</TableCell>
                          <TableCell className="truncate">
                            <div className="text-primary font-semibold">
                              {resolveProjectLabel(
                                record.project,
                                projectSelectOptions
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-blue-600">
                            {invoice.period}
                          </TableCell>
                          <TableCell className="text-right">
                            {inr(invoice.amount)}
                          </TableCell>
                          <TableCell className="text-right">
                            {inr(invoice.gst)}
                          </TableCell>
                          <TableCell className="text-right font-semibold">
                            {inr(invoice.total)}
                          </TableCell>
                          <TableCell>
                            {invoiceStatusBadge(invoice.status)}
                          </TableCell>
                          <TableCell className="py-2 text-center">
                            <div
                              className="flex items-center justify-center"
                              onClick={event => event.stopPropagation()}
                            >
                              {followupStatus === "in-progress" &&
                              apiRecordId ? (
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  className={`${INVOICE_FOLLOWUP_ACTION_BTN_CLASS} border-orange-400/50 bg-orange-500/10 text-orange-700 hover:border-orange-500/60 hover:bg-orange-500/15 hover:text-orange-800`}
                                  onClick={() => {
                                    setFollowupInvoice({
                                      recordId: apiRecordId,
                                      invoice,
                                    });
                                  }}
                                >
                                  <Mail className="h-3.5 w-3.5 shrink-0 text-orange-600" />
                                  Sent
                                </Button>
                              ) : followupStatus === "draft" ? (
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  disabled
                                  className={INVOICE_FOLLOWUP_ACTION_BTN_CLASS}
                                >
                                  <Mail className="h-3.5 w-3.5 shrink-0" />
                                  Sent
                                </Button>
                              ) : followupStatus === "send" ? (
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  disabled
                                  className={`${INVOICE_FOLLOWUP_ACTION_BTN_CLASS} cursor-default border-green-500/30 bg-green-500/10 text-green-700 opacity-100 hover:bg-green-500/10 hover:text-green-700 disabled:pointer-events-auto disabled:cursor-default disabled:opacity-100`}
                                  title="Follow-up sent"
                                >
                                  <MailCheck className="h-3.5 w-3.5 shrink-0 text-green-600" />
                                  Sent
                                </Button>
                              ) : (
                                <span className="text-muted-foreground text-xs">
                                  —
                                </span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="py-2 pl-2 pr-3">
                            <div
                              className={`ml-auto flex ${INVOICE_ACTIONS_WIDTH_CLASS} items-center justify-end gap-1`}
                              onClick={event => event.stopPropagation()}
                            >
                              {canMarkAsSent ? (
                                <>
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    className={INVOICE_MAIL_ACTION_BTN_CLASS}
                                    onClick={() => {
                                      handleMailSentClick(
                                        apiRecordId!,
                                        invoice,
                                        {
                                          isRecurring: Boolean(
                                            record.is_recurring
                                          ),
                                          hasInvoiceNumber: Boolean(
                                            record.invoice_number?.trim()
                                          ),
                                        }
                                      );
                                    }}
                                  >
                                    <Mail className="h-3.5 w-3.5 shrink-0" />
                                    Mail Sent
                                  </Button>
                                  <Popconfirm
                                    title="Are you sure you want to delete this invoice?"
                                    onConfirm={() =>
                                      deleteInvoiceMutation.mutate(apiRecordId!)
                                    }
                                    okText="Yes"
                                    cancelText="No"
                                  >
                                    <span
                                      className={INVOICE_ACTION_ICON_SLOT_CLASS}
                                    >
                                      <CustomButton
                                        danger
                                        icon={<Trash size={14} />}
                                        type="text"
                                        title="Delete invoice"
                                        loading={
                                          deleteInvoiceMutation.isPending
                                        }
                                      />
                                    </span>
                                  </Popconfirm>
                                </>
                              ) : isSent ? (
                                <>
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    disabled
                                    className={`${INVOICE_MAIL_ACTION_BTN_CLASS} cursor-default border-green-500/30 bg-green-500/10 text-green-700 opacity-100 hover:bg-green-500/10 hover:text-green-700 disabled:opacity-100`}
                                  >
                                    <Mail className="h-3.5 w-3.5 shrink-0 text-green-600" />
                                    Mail Sent
                                  </Button>
                                  <span
                                    className={INVOICE_ACTION_ICON_SLOT_CLASS}
                                    title="Email sent"
                                  >
                                    <CheckCircle2 className="h-4 w-4 animate-pulse text-green-600" />
                                  </span>
                                </>
                              ) : (
                                <span className="text-muted-foreground w-full text-center text-xs">
                                  —
                                </span>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                        {isExpanded ? (
                          <TableRow
                            key={`${recordId}-details`}
                            className="bg-primary/5 hover:bg-primary/5"
                          >
                            <TableCell
                              colSpan={INVOICE_PREVIEW_COLUMN_COUNT}
                              className="bg-muted/10 px-3 py-3 sm:px-4"
                            >
                              <InvoicePreviewExpandedDetails record={record} />
                            </TableCell>
                          </TableRow>
                        ) : null}
                      </Fragment>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
          <BillingInvoicePagination
            current={page}
            pageSize={pageSize}
            total={totalCount}
            onChange={(nextPage, nextPageSize) => {
              setPage(nextPage);
              setPageSize(nextPageSize);
            }}
            disabled={isLoading}
          />
        </CardContent>
      </Card>

      <InvoiceNumberUpdateModal
        open={Boolean(invoiceNumberUpdateInvoice)}
        invoice={invoiceNumberUpdateInvoice?.invoice ?? null}
        isSubmitting={updateInvoiceNumberMutation.isPending}
        errorMessage={invoiceNumberError}
        onOpenChange={open => {
          if (!open && !updateInvoiceNumberMutation.isPending) {
            setInvoiceNumberError(null);
            setInvoiceNumberUpdateInvoice(null);
          }
        }}
        onSave={handleSaveInvoiceNumber}
        onInputChange={() => setInvoiceNumberError(null)}
      />

      <InvoiceMailSentConfirmModal
        open={Boolean(mailSentInvoice) && !mailSendingOverlay}
        invoice={mailSentInvoice?.invoice ?? null}
        isSubmitting={mailSentMutation.isPending}
        onOpenChange={open => {
          if (!open && !mailSendingOverlay) {
            setMailSentInvoice(null);
          }
        }}
        onConfirm={handleConfirmMailSent}
      />

      <InvoiceFollowupConfirmModal
        open={Boolean(followupInvoice) && !followupSendingOverlay}
        invoice={followupInvoice?.invoice ?? null}
        isSubmitting={followupMutation.isPending}
        onOpenChange={open => {
          if (!open && !followupSendingOverlay) {
            setFollowupInvoice(null);
          }
        }}
        onConfirm={handleConfirmFollowup}
      />

      <InvoiceMailSendingOverlay
        open={Boolean(mailSendingOverlay)}
        status={mailSendingOverlay ?? "sending"}
      />

      <InvoiceMailSendingOverlay
        open={Boolean(followupSendingOverlay)}
        status={followupSendingOverlay ?? "sending"}
      />
    </>
  );
};
